import { BigNumber } from 'ethers'
import { DateTime } from 'luxon'
import { MAX_MEMORIES_PER_TROPHY, TrophyMemory } from '~/modules/TrophyMemories/Domain/TrophyMemory'
import Relation from '~/modules/Relations/Domain/Relation'
import TrophyDomainException from '~/modules/Trophies/Domain/TrophyDomainException'
import { Organizer } from '~/modules/Organizers/Domain/Organizer'
import { extname } from 'path'
import { Collection, LEVERADE_COLLECTION_ID } from '~/modules/Products/Domain/Collection'
import { User } from '~/modules/Auth/Domain/User'

enum ValidMintStatus {
  PENDING = 'pending',
  MINTING = 'minting',
  MINTED = 'minted'
}

export type MintStatus = `${ValidMintStatus}`;

export interface TrophyAttributeInterface {
  trait_type: 'Discipline' | 'Competition' | 'Full Name' | 'Award Type' | 'Date' | string
  value: string
}

export class Trophy {
  // TODO: Find the way to avoid this situation. Maybe we need the domain to create the id
  /** Internal trophy id */
  private _id: BigNumber | null

  /** Profile id in LEVERADE of the athlete that won the trophy */
  public readonly profileId: number | null

  /** Id in LEVERADE of the achievement that triggered winning the trophy */
  public readonly resultId: number | null

  /** Collection ID */
  public readonly collectionId: string

  /**
   * Id of the nft in which this trophy is (to be) minted
   *
   * For Series collections we don't know the token id in advance, so it must be null in those cases.
   */
  public readonly tokenId: BigNumber | null

  /** Name of the trophy / nft */
  public readonly name: string

  /** Description of the trophy / nft */
  public readonly description: string

  /** Image URI of the trophy / nft */
  public readonly image: string

  /** Attributes of the trophy / nft */
  public readonly attributes: TrophyAttributeInterface[]

  /** Whether the trophy has been minted, is being minted, or it's not minted */
  public mintStatus: MintStatus

  /** Animation URI of the trophy / nft */
  public readonly animationUrl: string | null

  /** Memories of the trophy */
  private readonly _memories: Relation<TrophyMemory[]>

  /** Trophy's owner user id */
  public readonly userId: string | null

  /** The raw image of the trophy, without the borders and extra info */
  public readonly rawImageUrl: string

  /** Trophy's organizer id */
  public readonly organizerId: string | null

  /** Organizer of the trophy */
  private _organizer: Relation<Organizer>

  /** Organizer of the trophy */
  private _collection: Relation<Collection>

  /** Card creation date */
  private readonly _createdAt: DateTime | null

  /** Card update date */
  private readonly _updatedAt: DateTime | null

  /** Aggregated count of memories for list views when relation is not loaded */
  private readonly _memoriesCount: number | null

  constructor (
    id: BigNumber | null,
    collectionId: string | null,
    tokenId: BigNumber | null,
    name: string,
    description: string,
    image: string,
    attributes: TrophyAttributeInterface[],
    mintStatus = 'pending',
    animationUrl: string | null,
    memories: Relation<TrophyMemory[]>,
    rawImageUrl: string,
    organizerId: string | null,
    userId: string | null,
    profileId: number | null,
    resultId: number | null,
    createdAt: DateTime | null,
    updatedAt: DateTime | null,
    memoriesCount: number | null = null
  ) {
    this._id = id
    this.profileId = profileId
    this.resultId = resultId
    this.collectionId = collectionId ?? LEVERADE_COLLECTION_ID
    this.tokenId = tokenId
    this.name = name
    this.description = description
    this.image = image
    this.attributes = attributes
    this.mintStatus = Trophy.validateMintStatus(mintStatus)
    this.animationUrl = animationUrl
    this._memories = memories
    this.rawImageUrl = rawImageUrl
    this.userId = userId
    this.organizerId = organizerId
    this._organizer = Relation.notLoaded()
    this._collection = Relation.notLoaded()
    this._createdAt = createdAt
    this._updatedAt = updatedAt
    this._memoriesCount = memoriesCount
  }

  public findAttribute (trait: TrophyAttributeInterface['trait_type']): TrophyAttributeInterface | undefined {
    return this.attributes.find(
      ({ trait_type: traitType }) => traitType.toLowerCase() === trait.toLowerCase()
    )
  }

  get id (): BigNumber | null {
    return this._id
  }

  get date (): DateTime | null {
    const date = this.findAttribute('Date')

    if (date) {
      return DateTime.fromSQL(date.value)
    }

    return null
  }

  get memories (): TrophyMemory[] {
    return this._memories.value
  }

  get memoriesCount (): number {
    try {
      return this._memories.value.length
    } catch (exception: unknown) {
      return this._memoriesCount ?? 0
    }
  }

  get organizer (): Organizer {
    return this._organizer.value
  }

  get collection (): Collection {
    return this._collection.value
  }

  private static validateMintStatus (status: string): MintStatus {
    const values: string [] = Object.values(ValidMintStatus)

    if (!values.includes(status)) {
      throw TrophyDomainException.invalidMintStatus()
    }

    return status as MintStatus
  }

  get createdAt (): DateTime | null {
    return this._createdAt
  }

  get updatedAt (): DateTime | null {
    return this._updatedAt
  }

  get externalUrl (): string {
    if (!this.id) throw new Error('Cannot generate external url for trophy without id')

    return `${process.env.BASE_URL}/trophies/${this.id}`
  }

  get metadataUrl (): string {
    if (!this.id) throw new Error('Cannot generate metadata url for trophy without id')

    return `${process.env.BASE_URL}/api/series/metadata/trophies/${this.id}`
  }

  public assertMintIsAllowedFor (userId: string): void {
    if (this.mintStatus !== 'pending') {
      throw TrophyDomainException.notMintable()
    }

    if (userId !== this.userId) {
      throw TrophyDomainException.userCannotMintTrophy()
    }
  }

  public startMintingBy (userId: string): void {
    this.assertMintIsAllowedFor(userId)

    this.mintStatus = 'minting'
  }

  public finishMinting (): void {
    if (this.mintStatus !== 'minting') {
      throw TrophyDomainException.notInMintingProcess()
    }

    this.mintStatus = 'minted'
  }

  public abortMinting (): void {
    if (this.mintStatus !== 'minting') {
      throw TrophyDomainException.notInMintingProcess()
    }

    this.mintStatus = 'pending'
  }

  public getAnimationUrlVideoType (): string {
    if (this.animationUrl === null) {
      return ''
    }

    const extension = extname(this.animationUrl).substring(1)

    return `video/${extension}`
  }

  public setOrganizer (organizer: Organizer): Trophy {
    this._organizer = Relation.from(organizer)

    return this
  }

  public setCollection (collection: Collection): Trophy {
    this._collection = Relation.from(collection)

    return this
  }

  public setId (id: BigNumber): Trophy {
    if (this._id) throw new Error('Cannot reassign trophy id')

    this._id = id

    return this
  }

  public assertUserCanAddMemories (user: User): void {
    const trophyId = this.id ? this.id.toString() : ''

    if (this.userId !== user.id) {
      throw TrophyDomainException.doesNotBelongToUser(trophyId, user.id)
    }

    if (this._memories.value.length >= MAX_MEMORIES_PER_TROPHY) {
      throw TrophyDomainException.cannotAddMemory(trophyId)
    }
  }
}
